import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Box, Group, ScrollArea } from '@mantine/core'
import { AppButton, AppText } from '../../app/common/ui'
import agent from '../../app/api/agent'
import type { PollListItemDto } from '../../app/models/poll'
import { useStore } from '../../app/stores/store'
import { canCreatePoll } from '../../app/common/constants/reputation'

function timeLeft (expiresAt: string): string {
  const ms = new Date(expiresAt).getTime() - Date.now()
  if (ms <= 0) return 'Ended'
  const h = Math.floor(ms / 3600000)
  const m = Math.floor((ms % 3600000) / 60000)
  return h > 0 ? `${h}h ${m}m` : `${m}m`
}

/** Sitewide active polls for home feed / discover surfaces (Phase 0). Fails silent when polls-enabled is off. */
export default function ActivePollsStrip () {
  const { userStore } = useStore()
  const [polls, setPolls] = useState<PollListItemDto[]>([])
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    agent.Polls.list({ active: true, pageNumber: 1, pageSize: 8 })
      .then((r) => {
        if (r.items.length > 0) {
          setPolls(r.items)
          setVisible(true)
        }
      })
      .catch(() => setVisible(false))
  }, [])

  if (!visible || polls.length === 0) return null

  return (
    <Box
      mb="md"
      p="md"
      sx={{
        border: '1px solid var(--gs-border)',
        borderRadius: 8,
        background: 'var(--gs-surface)'
      }}
    >
      <Group position="apart" mb="sm">
        <AppText sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 600, fontSize: 14 }}>
          ACTIVE POLLS
        </AppText>
        <Group spacing="xs">
          {userStore.isLoggedIn && canCreatePoll(userStore.user) && (
            <AppButton component={Link} to="/polls/new" size="xs" variant="light" color="ember">
              Create
            </AppButton>
          )}
          <AppButton component={Link} to="/polls" size="xs" variant="subtle">
            All polls
          </AppButton>
        </Group>
      </Group>
      <ScrollArea type="scroll" offsetScrollbars>
        <Group spacing="sm" noWrap sx={{ paddingBottom: 4 }}>
          {polls.map((p) => (
            <Box
              key={p.id}
              component={Link}
              to={`/polls/${p.id}`}
              sx={{
                minWidth: 220,
                maxWidth: 280,
                padding: 12,
                borderRadius: 8,
                border: '1px solid var(--gs-border)',
                textDecoration: 'none',
                color: 'inherit',
                background: 'var(--gs-surface2)',
                '&:hover': { borderColor: 'var(--gs-accent)' }
              }}
            >
              <AppText size="sm" weight={600} lineClamp={2}>
                {p.question}
              </AppText>
              <AppText size="xs" color="dimmed" mt={4}>
                {timeLeft(p.expiresAt)}
                {p.totalVotes != null ? ` · ${p.totalVotes} votes` : ''}
                {p.optionCount > 0 ? ` · ${p.optionCount} options` : ''}
              </AppText>
            </Box>
          ))}
        </Group>
      </ScrollArea>
    </Box>
  )
}
